import {
  apiErrorHasCode,
  ClientContact,
  ClientContactConversation,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useCallback } from "react";

export type CreateContactInput = {
  name: string;
  email?: string;
  phone?: string;
  conversations?: ClientContactConversation[];
};

interface UseCreateContactOptions {
  entityId: string;
}

export const useCreateContact = ({ entityId }: UseCreateContactOptions) => {
  const { contactsApi } = useRestApiProvider();

  const [createContactMutation, { isLoading }] =
    contactsApi.useCreateContactMutation();

  const { t } = useTranslations();
  const { toast } = useToast();

  const createContact = useCallback(
    async (input: CreateContactInput): Promise<ClientContact | null> => {
      const response = await createContactMutation({
        entityId,
        data: {
          name: input.name.trim(),
          email: input.email?.trim() || undefined,
          phone: input.phone?.trim() || undefined,
          conversations: input.conversations,
        },
        meta: {
          getErrorMessage: (error) =>
            apiErrorHasCode(error, "email_or_phone_already_exists")
              ? t({
                  id: "client-support-page.contact-email-or-phone-already-exists",
                  defaultMessage:
                    "This email or phone number is already used by another contact",
                })
              : undefined,
        },
      });

      if (response && "data" in response) {
        const contactName = input.name.trim() || response.data.name || "";

        toast(
          <span>
            {t(
              {
                id: "client-support-page.contact-added-to-contacts",
                defaultMessage: "{name} was successfully added to Contacts",
              },
              {
                name: <span className="font-medium">{contactName}</span>,
              }
            )}
          </span>
        );

        return response.data;
      }

      return null;
    },
    [createContactMutation, entityId, t, toast]
  );

  return {
    isLoading,
    createContact,
  };
};
